import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Users, Download, Copy, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

// Saisonale Vorlagen — Brand-Style Marketing-Templates
const SEASONAL_TEMPLATES = [
  {
    id: 'spring', emoji: '🌷', label: 'Frühling',
    subject: '🌷 Frühlings-Highlights bei Homeyoursoul',
    content: `Liebe Homeyoursoul-Familie,

der Frühling ist da! 🌸

Pünktlich zur ersten Sonnenstrahlen-Saison haben wir frische Designs und farbenfrohe Accessoires für dich vorbereitet. Pastelltöne, Blütenmotive und alles, was Lust auf Leichtigkeit macht.

✨ Frühlings-Drop: Neue Haargummi-Sets in Pastell
✨ Limited: Kollektion „Blossom Delight"
✨ Exklusiv: 10 % Rabatt mit Code FRÜHLING10

Schau gerne vorbei — wir freuen uns auf dich!

Herzliche Grüße
Yasemin & das Homeyoursoul-Team`,
  },
  {
    id: 'summer', emoji: '☀️', label: 'Sommer',
    subject: '☀️ Sommer-Vibes — Hol dir das Urlaubsgefühl',
    content: `Hallo du Sonnenkind,

der Sommer ruft! 🌞 Zeit für leichte Frisuren, bunte Accessoires und gute Laune.

🌺 Neu: „Hot Sunset"-Kollektion mit Rubin-Tönen
🌺 Sommer-Sale: bis zu 20 % auf ausgewählte Artikel
🌺 Versandkostenfrei ab 30 €

Mach dir einen wunderschönen Sommer — wir haben das passende Accessoire dazu!

Sonnige Grüße
Yasemin von Homeyoursoul`,
  },
  {
    id: 'autumn', emoji: '🍂', label: 'Herbst',
    subject: '🍂 Cozy Autumn — Warme Farben für goldene Tage',
    content: `Liebe Freundin von Homeyoursoul,

der Herbst zaubert die schönsten Farben in die Natur — und wir bringen sie in dein Haar! 🍁

🤎 Neu: „Mocha Vintage"-Kollektion
🤎 Boho Nature: Erdtöne, die jeden Look erden
🤎 Cozy-Bundle: 3 für 2 auf allen Strick-Scrunchies

Lass dich vom Herbst inspirieren — bei uns wartet warmer Stil.

Warme Grüße
Dein Homeyoursoul-Team`,
  },
  {
    id: 'christmas', emoji: '🎄', label: 'Weihnachten',
    subject: '🎄 Weihnachts-Wunder bei Homeyoursoul',
    content: `Liebe Homeyoursoul-Familie,

die schönste Zeit des Jahres beginnt! 🎁

Unter dem Tannenbaum darf etwas Funkelndes nicht fehlen — wir haben dir die zauberhaftesten Geschenkideen liebevoll zusammengestellt:

✨ Geschenkboxen ab 9,99 € (rundliche Kollektionsboxen)
✨ „Shiny Glam" — funkelt unterm Christbaum
✨ Versandkostenfrei + Geschenkverpackung gratis bis 18.12.

Frohe Vorfreude!
Yasemin & Team Homeyoursoul`,
  },
  {
    id: 'valentine', emoji: '💕', label: 'Valentinstag',
    subject: '💕 Liebevolle Geschenke zum Valentinstag',
    content: `Hallo du Liebe,

der Tag der Liebe steht vor der Tür — und auch das schönste Geschenk darf etwas Persönliches haben. 💝

💖 „Blossom Delight" — verspielt und romantisch
💖 Herz-Designs in unserer Sondereinheit
💖 Code LOVE15 für 15 % Rabatt

Schenk Liebe — auch dir selbst. Du verdienst es!

Mit Herz,
Yasemin von Homeyoursoul`,
  },
  {
    id: 'easter', emoji: '🐰', label: 'Ostern',
    subject: '🐰 Frohe Ostern — Frühlingshafte Überraschungen',
    content: `Liebe Homeyoursoul-Familie,

bald hoppelt der Osterhase! 🌷

Verstecke nicht nur Eier — sondern auch zauberhafte Überraschungen für deine Liebsten:

🥚 Mini Collection Boxes als Geschenk
🥚 Pastell-Sets — perfekt für den Osterspaziergang
🥚 Frühlings-Vibes in jeder Box

Wir wünschen dir wunderschöne Ostertage!

Yasemin & Team Homeyoursoul`,
  },
  {
    id: 'blackfriday', emoji: '🛍️', label: 'Black Friday',
    subject: '🛍️ Black Friday Deals — Bis zu 30 % Rabatt!',
    content: `Hallo Schatz,

Black Friday ist da! 🖤

Heute (und nur heute) gibt's unsere besten Preise des Jahres:

🔥 Bis zu 30 % auf ausgewählte Sets
🔥 Code BF30 — direkt im Warenkorb einlösbar
🔥 Versandkostenfrei ab 25 €
🔥 Nur solange Vorrat reicht!

Schnapp dir, was du schon lange wolltest!

Herzliche Grüße
Yasemin von Homeyoursoul`,
  },
  {
    id: 'newdrop', emoji: '✨', label: 'Neuer Drop',
    subject: '✨ Frisch eingetroffen — Schau es dir an!',
    content: `Liebe Homeyoursoul-Familie,

es ist soweit — der neue Drop ist online! 🎉

Liebevoll handverlesen, sorgsam gestaltet und nur in limitierter Stückzahl verfügbar:

🌸 Neue Designs in unserer aktuellen Kollektion
🌸 Frische Farben, neue Lieblinge
🌸 Sicher dir deine Favoriten, bevor sie weg sind

Schau vorbei — wir freuen uns auf dich!

Mit Liebe,
Yasemin & das Homeyoursoul-Team`,
  },
];

const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsletterForm, setNewsletterForm] = useState({ subject: '', content: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsRes, newsRes] = await Promise.all([
        axiosInstance.get('/newsletter/subscribers'),
        axiosInstance.get('/newsletter/templates')
      ]);
      setSubscribers(subsRes.data || []);
      setNewsletters(newsRes.data || []);
    } catch (error) {
      console.error('Error loading newsletter data:', error);
      // Don't show error toast, just set empty arrays
      setSubscribers([]);
      setNewsletters([]);
    } finally {
      setLoading(false);
    }
  };

  // Export emails as CSV
  const handleExportCSV = () => {
    if (subscribers.length === 0) {
      toast.error('Keine Abonnenten zum Exportieren');
      return;
    }
    
    const csv = 'E-Mail,Anmeldedatum\n' + subscribers.map(s => 
      `${s.email},${new Date(s.subscribed_at || s.created_at).toLocaleDateString('de-DE')}`
    ).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-abonnenten-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exportiert!');
  };

  // Copy all emails to clipboard
  const handleCopyEmails = () => {
    if (subscribers.length === 0) {
      toast.error('Keine Abonnenten zum Kopieren');
      return;
    }
    
    const emails = subscribers.map(s => s.email).join(', ');
    navigator.clipboard.writeText(emails);
    toast.success(`${subscribers.length} E-Mail-Adressen kopiert!`);
  };

  const handleCreateNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterForm.subject || !newsletterForm.content) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    setSending(true);
    try {
      await axiosInstance.post('/admin/newsletter', newsletterForm);
      toast.success(`Newsletter erstellt!`);
      setNewsletterForm({ subject: '', content: '' });
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Erstellen des Newsletters');
    } finally {
      setSending(false);
    }
  };

  const handleSendNewsletter = async (newsletterId) => {
    if (!window.confirm(`Newsletter an ${subscribers.length} Abonnenten versenden?`)) return;
    
    setSending(true);
    try {
      const response = await axiosInstance.post(`/admin/newsletter/${newsletterId}/send`);
      toast.success(`Newsletter versendet! ${response.data.sent_count} E-Mails erfolgreich gesendet.`);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Versenden des Newsletters';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 lg:mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Newsletter-Verwaltung</h1>
        </div>

        <Tabs defaultValue="editor" className="w-full">
          <TabsList className="grid w-full grid-cols-3 text-xs sm:text-sm">
            <TabsTrigger value="editor">Newsletter erstellen</TabsTrigger>
            <TabsTrigger value="subscribers">Abonnenten ({subscribers.length})</TabsTrigger>
            <TabsTrigger value="sent">Versendete Newsletter</TabsTrigger>
          </TabsList>

          {/* Newsletter Editor */}
          <TabsContent value="editor">
            <Card>
              <CardHeader>
                <CardTitle>Newsletter gestalten</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateNewsletter} className="space-y-4">
                  {/* Saisonale Vorlagen */}
                  <div>
                    <Label className="text-[#5D4E3C] font-semibold">📚 Vorlage auswählen (optional)</Label>
                    <p className="text-xs text-[#8B7355] mb-2">Klick auf eine Vorlage, dann anpassen — fertig.</p>
                    <div className="flex gap-2 flex-wrap">
                      {SEASONAL_TEMPLATES.map(t => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            if (newsletterForm.subject || newsletterForm.content) {
                              if (!window.confirm('Aktuellen Text durch Vorlage ersetzen?')) return;
                            }
                            setNewsletterForm({ subject: t.subject, content: t.content });
                            toast.success(`Vorlage „${t.label}" geladen`);
                          }}
                          className="px-3 py-1.5 text-xs border border-[#C4A084] bg-white hover:bg-[#F5EDE4] text-[#5D4E3C] flex items-center gap-1"
                          data-testid={`newsletter-template-${t.id}`}
                        >
                          <span className="text-base">{t.emoji}</span> {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Split: Editor (left) | Live-Vorschau (right) */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="subject">Betreff *</Label>
                        <Input
                          id="subject"
                          required
                          value={newsletterForm.subject}
                          onChange={(e) => setNewsletterForm({ ...newsletterForm, subject: e.target.value })}
                          placeholder="z.B. ✨ Neue Drops im Frühling"
                        />
                      </div>
                      <div>
                        <Label htmlFor="content">Inhalt *</Label>
                        <Textarea
                          id="content"
                          required
                          value={newsletterForm.content}
                          onChange={(e) => setNewsletterForm({ ...newsletterForm, content: e.target.value })}
                          rows={16}
                          placeholder="Schreiben Sie hier Ihren Newsletter-Text — oder wähle eine Vorlage oben."
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                    {/* Live-Vorschau */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Eye className="h-4 w-4 text-[#5D4E3C]" />
                        <Label className="text-[#5D4E3C] font-semibold m-0">Live-Vorschau</Label>
                      </div>
                      <p className="text-xs text-[#8B7355] mb-2">So sieht's für deine Empfänger aus.</p>
                      <div className="border border-[#C4A084] bg-white p-0 overflow-hidden">
                        {/* Mail-Header */}
                        <div className="px-4 py-3 border-b border-[#E0CFB8] bg-[#FAF5EE]">
                          <p className="text-[10px] uppercase tracking-wider text-[#8B7355]">Betreff</p>
                          <p className="text-sm font-semibold text-[#5D4E3C] truncate">
                            {newsletterForm.subject || <span className="italic text-[#9C8C7A]">(noch leer)</span>}
                          </p>
                        </div>
                        {/* Mail-Body */}
                        <div className="px-4 py-4 max-h-[420px] overflow-y-auto" style={{ backgroundColor: '#FFFFFF' }}>
                          {/* Brand-Kopfzeile */}
                          <div className="text-center py-3 mb-3" style={{ background: 'linear-gradient(135deg, #5D4E3C, #AF9774)' }}>
                            <p className="text-white text-[10px] tracking-[4px] uppercase">❀ Homeyoursoul ❀</p>
                          </div>
                          {newsletterForm.content ? (
                            <p className="text-[#2B2018] text-sm whitespace-pre-line leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                              {newsletterForm.content}
                            </p>
                          ) : (
                            <p className="text-[#9C8C7A] italic text-sm text-center py-8">
                              Hier erscheint deine Vorschau, sobald du Text eingibst…
                            </p>
                          )}
                          {/* Brand-Footer */}
                          <div className="mt-6 pt-3 border-t border-[#E0CFB8] text-center">
                            <p className="text-xs text-[#8B7355]">www.homeyoursoul.com</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-none p-4">
                    <p className="text-sm text-amber-800">
                      <strong>💡 Tipp für manuellen Newsletter-Versand:</strong><br/>
                      1. Klicken Sie auf "Kopieren" um alle E-Mail-Adressen zu kopieren<br/>
                      2. Öffnen Sie Gmail/Outlook und erstellen Sie eine neue E-Mail<br/>
                      3. Fügen Sie die Adressen im BCC-Feld ein (damit Empfänger die anderen nicht sehen)<br/>
                      4. Schreiben Sie Ihren Newsletter und versenden Sie ihn
                    </p>
                  </div>
                  <Button type="submit" disabled={sending} className="w-full rounded-none bg-[#E3F2FD] text-[#2196F3] hover:bg-[#BBDEFB] border-0">
                    <Send className="h-4 w-4 mr-2" />
                    {sending ? 'Wird erstellt...' : 'Newsletter erstellen'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscribers List */}
          <TabsContent value="subscribers">
            <Card>
              <CardHeader>
                <CardTitle>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Newsletter-Abonnenten ({subscribers.length})
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleCopyEmails}
                        size="sm"
                        className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Kopieren
                      </Button>
                      <Button
                        onClick={handleExportCSV}
                        size="sm"
                        className="rounded-none bg-[#E3F2FD] text-[#2196F3] hover:bg-[#BBDEFB] border-0"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        CSV
                      </Button>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-gray-600 text-center py-8">Lädt...</p>
                ) : subscribers.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">Keine Abonnenten vorhanden</p>
                ) : (
                  <div className="space-y-2">
                    {subscribers.map((subscriber) => (
                      <div
                        key={subscriber.id}
                        className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div>
                          <p className="font-medium">{subscriber.email}</p>
                          {subscriber.name && (
                            <p className="text-sm text-gray-600">{subscriber.name}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            {formatDate(subscriber.subscribed_at)}
                          </p>
                          {!subscriber.discount_code_used && (
                            <p className="text-xs text-green-600">10% Code nicht verwendet</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sent Newsletters */}
          <TabsContent value="sent">
            <Card>
              <CardHeader>
                <CardTitle>Versendete Newsletter</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-gray-600 text-center py-8">Lädt...</p>
                ) : newsletters.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">Keine Newsletter versendet</p>
                ) : (
                  <div className="space-y-4">
                    {newsletters.map((newsletter) => (
                      <div
                        key={newsletter.id}
                        className="p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="font-semibold">{newsletter.subject}</h3>
                            <p className="text-sm text-gray-600">
                              Erstellt: {formatDate(newsletter.created_at)}
                            </p>
                            {newsletter.sent_at && (
                              <p className="text-sm text-green-600">
                                ✓ Versendet: {formatDate(newsletter.sent_at)}
                              </p>
                            )}
                          </div>
                          {!newsletter.sent_at && (
                            <Button
                              onClick={() => handleSendNewsletter(newsletter.id)}
                              disabled={sending || subscribers.length === 0}
                              size="sm"
                            >
                              <Send className="h-4 w-4 mr-2" />
                              An {subscribers.length} Abonnenten senden
                            </Button>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-3">
                          {newsletter.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminNewsletter;